import { useEffect, useState } from "react";
import { api, fileUrl, ACTING_KEY } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { OrdersBoard } from "@/components/admin/OrdersBoard";
import { MenuManager } from "@/components/admin/MenuManager";
import { BrandingEditor } from "@/components/admin/BrandingEditor";
import { QRGenerator } from "@/components/admin/QRGenerator";
import { LoyaltyManager } from "@/components/admin/LoyaltyManager";
import { InsightsDashboard } from "@/components/admin/InsightsDashboard";
import { SetupPanel } from "@/components/admin/SetupPanel";
import { StaffManager } from "@/components/admin/StaffManager";
import { StaffScanPanel } from "@/components/admin/StaffScanPanel";
import { LayoutDashboard, UtensilsCrossed, Palette, QrCode, LogOut, ChefHat, Loader2, Gift, BarChart3, ShieldCheck, Users, ScanLine } from "lucide-react";

const TABS = [
  { key: "orders", label: "Live Orders", icon: LayoutDashboard },
  { key: "insights", label: "Insights", icon: BarChart3 },
  { key: "menu", label: "Menu", icon: UtensilsCrossed },
  { key: "loyalty", label: "Loyalty", icon: Gift },
  { key: "branding", label: "Brand", icon: Palette },
  { key: "qr", label: "QR Codes", icon: QrCode },
  { key: "staff", label: "Staff", icon: Users },
];
const STAFF_TABS = [
  { key: "orders", label: "Live Orders", icon: LayoutDashboard },
  { key: "menu", label: "Menu", icon: UtensilsCrossed },
  { key: "scan", label: "Scan stamps", icon: ScanLine },
];
const SETUP_TAB = { key: "setup", label: "Setup", icon: ShieldCheck };

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("orders");
  const [restaurant, setRestaurant] = useState(null);
  const isPlatform = user?.role === "platform_admin";
  const isStaff = user?.role === "staff";
  const tabs = isStaff ? STAFF_TABS : isPlatform ? [...TABS, SETUP_TAB] : TABS;
  const can = (k) => tabs.some((t) => t.key === k);

  useEffect(() => {
    api.get("/my/restaurant").then((res) => setRestaurant(res.data));
  }, []);

  if (!restaurant) {
    return (
      <div className="min-h-screen bg-slate-950 grid place-items-center">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex flex-col md:flex-row">
      <aside className="w-full md:w-64 bg-slate-900/95 backdrop-blur border-b md:border-b-0 md:border-r border-slate-800 px-4 pt-3 pb-2 md:p-6 flex md:flex-col justify-between shrink-0 sticky top-0 z-40 md:static md:h-auto">
        <div className="w-full min-w-0">
          <div className="flex items-center gap-2 mb-2 md:mb-8">
            <div className="h-8 w-8 md:h-10 md:w-10 rounded-xl overflow-hidden bg-orange-600 grid place-items-center shrink-0">
              {restaurant.logo_path ? <img src={fileUrl(restaurant.logo_path)} alt="logo" className="h-full w-full object-cover" /> : <ChefHat className="h-5 w-5 md:h-6 md:w-6 text-white" />}
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-sub font-bold truncate text-sm md:text-base">{restaurant.name}</p>
              <p className="hidden md:block text-xs text-slate-500 truncate">{user?.email}</p>
              {isPlatform && <p data-testid="platform-admin-badge" className="text-[10px] uppercase tracking-wider text-orange-400">Platform admin</p>}
            </div>
            <button data-testid="logout-button-mobile" onClick={logout} aria-label="Sign out" className="md:hidden h-9 w-9 grid place-items-center rounded-xl text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
          <nav className="flex md:flex-col gap-1 overflow-x-auto no-scrollbar -mx-4 px-4 md:mx-0 md:px-0 snap-x">
            {tabs.map((t) => (
              <button
                key={t.key}
                data-testid={`tab-${t.key}`}
                onClick={(e) => { setTab(t.key); e.currentTarget.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" }); }}
                className={`flex items-center gap-2 md:gap-3 rounded-xl px-3 py-2 md:px-4 md:py-3 text-sm font-medium transition-colors shrink-0 snap-start ${tab === t.key ? "bg-orange-600 text-white" : "text-slate-400 hover:bg-slate-800 hover:text-white"}`}
              >
                <t.icon className="h-4 w-4" /> {t.label}
              </button>
            ))}
          </nav>
        </div>
        <button data-testid="logout-button" onClick={logout} className="hidden md:flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium text-slate-400 hover:bg-slate-800 hover:text-white transition-colors">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </aside>

      <main className="flex-1 min-w-0 p-4 md:p-10 overflow-y-auto">
        {isPlatform && localStorage.getItem(ACTING_KEY) && restaurant.id !== user.home_restaurant_id && (
          <div data-testid="acting-banner" className="mb-6 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-orange-800 bg-orange-950/40 px-4 py-3 text-sm">
            <span>Viewing <b>{restaurant.name}</b> as platform admin</span>
            <button data-testid="back-to-my-restaurant" onClick={() => { localStorage.removeItem(ACTING_KEY); window.location.reload(); }} className="rounded-lg bg-orange-600 hover:bg-orange-700 px-3 py-1.5 text-xs font-semibold transition-colors">
              Back to my restaurant
            </button>
          </div>
        )}
        <div className={tab === "orders" ? "" : "hidden"}><OrdersBoard currency={restaurant.currency_symbol} canDelete={!isStaff} /></div>
        {tab === "insights" && can("insights") && <InsightsDashboard accent={restaurant.primary_color} currency={restaurant.currency_symbol} />}
        {tab === "menu" && <MenuManager currency={restaurant.currency_symbol} staffMode={isStaff} />}
        {tab === "loyalty" && can("loyalty") && <LoyaltyManager restaurant={restaurant} />}
        {tab === "branding" && can("branding") && <BrandingEditor restaurant={restaurant} onUpdate={setRestaurant} />}
        {tab === "qr" && can("qr") && <QRGenerator restaurant={restaurant} />}
        {tab === "staff" && can("staff") && <StaffManager />}
        {tab === "scan" && isStaff && <StaffScanPanel accent={restaurant.primary_color} />}
        {tab === "setup" && isPlatform && <SetupPanel currentId={restaurant.id} />}
      </main>
    </div>
  );
}
